/**
 * ContactVerificationGate — conditional mount wrapper for the annual
 * contact-information verification prompt (US4, T040).
 *
 * Mounted on the post-login shell, INSIDE `OnboardingGate` (onboarding wins
 * first; verification only after a member has onboarded). Unlike
 * `OnboardingGate`, this gate always renders `children` and layers the prompt
 * as a portal OVERLAY on top — the prompt is a modal dialog, not a full-surface
 * takeover, so the shell stays mounted beneath it.
 *
 * Render gate (pass-through, prompt NOT mounted) when EITHER:
 *   - the session is not a member (admin is elevated capability — P4; anonymous
 *     hits `RouteGuard` which redirects to `/login`), OR
 *   - the `contactVerification` port slot is absent (a composition that does not
 *     wire the port never shows the prompt).
 *
 * Otherwise it renders `children` plus the prompt wrapped in a
 * `PortErrorBoundary(memberProfile)` + `<Suspense fallback={null}>`. Both are
 * required because `ContactVerificationPrompt` reads the verification state and
 * the LIVE member profile via `React.use()` (034 US2 made the profile read a
 * genuinely-async tRPC call): Suspense catches the pending read, and the error
 * boundary catches a REJECTED read (IS unavailable / not configured). On a
 * profile-read failure the nag simply does not show — the shell must never
 * crash because the annual verify prompt could not prefill (P7: degrade, not
 * fail). `fallback={null}` = skip the nag; the member proceeds to the app.
 *
 * Whether the prompt is VISIBLE (vs an invisible due-check) is the prompt's own
 * concern: it renders the dialog only when `state.isDue`. This gate only decides
 * whether the prompt is in the tree at all.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Task: T040
 */

import { type JSX, type ReactNode, Suspense, useContext } from 'react';
import { PortsContext } from '../providers/PortsContext.js';
import { useSession } from '../session/useSession.js';
import { PortErrorBoundary } from '../shell/PortErrorBoundary.js';
import { ContactVerificationPrompt } from './ContactVerificationPrompt.js';

export interface ContactVerificationGateProps {
  readonly children: ReactNode;
}

export function ContactVerificationGate({ children }: ContactVerificationGateProps): JSX.Element {
  const session = useSession();
  const ports = useContext(PortsContext);

  // Pass-through for non-members (admin — P4; anonymous — RouteGuard redirects)
  // and for compositions that do not wire the contact-verification port.
  if (session.state.kind !== 'member' || ports?.contactVerification === undefined) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <PortErrorBoundary portName="memberProfile" fallback={null}>
        <Suspense fallback={null}>
          <ContactVerificationPrompt />
        </Suspense>
      </PortErrorBoundary>
    </>
  );
}
